import { FC } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useController, useFormContext } from 'react-hook-form';
import startCase from 'lodash/startCase';
import { MemoizedBaseAvatar } from '@mgm/common/components/base';
import { useTranslation } from 'react-i18next';
import { TAvatarSelect } from './type';

const AvatarSelect: FC<TAvatarSelect> = ({
  options,
  value,
  onChange,
  maxHeight = 250,
  isRequired = false,
  size = 'small',
  sx,
}) => {
  const { control } = useFormContext();
  const { t } = useTranslation('team');

  const {
    field: { ref, ...selectProps },
  } = useController({
    control,
    name: 'avatar',
    rules: {
      required: isRequired,
    },
  });

  const handleChange = (event: SelectChangeEvent) => {
    const selectedOptionId = event.target.value;
    const selectedOption = options.find((option) => option.email === selectedOptionId) || null;

    selectProps.onChange(selectedOption);
    if (onChange) {
      onChange(selectedOption);
    }
  };

  return (
    <Select
      inputRef={ref}
      value={value?.email || ''}
      size={size}
      fullWidth
      onChange={handleChange}
      sx={sx}
      renderValue={(selectedValue) => {
        const selectedOption = options.find((option) => option.email === selectedValue);
        return selectedOption ? (
          <div className="align-center w-100">
            <MemoizedBaseAvatar
              size={{
                width: 24,
                height: 24,
              }}
              statusTooltipText={t('pendingRegistration')}
              status={selectedOption.status}
              avatarFile={selectedOption.avatarFile}
              avatarName={selectedOption.name}
            />
            <Typography fontSize={14}>
              {startCase(selectedOption.name)}
            </Typography>
          </div>
        ) : (
          // Display the userId if no matching member is found
          <Typography fontSize={14}>{selectedValue}</Typography>
        );
      }}
      MenuProps={{
        PaperProps: {
          style: {
            maxHeight,
          },
        },
      }}
    >
      {options.map((option, index) => (
        <MenuItem key={`${option.email}-${index?.toString()}`} value={option.email}>
          <div className="align-center w-100">
            <MemoizedBaseAvatar
              size={{
                width: 24,
                height: 24,
              }}
              status={option.status}
              avatarFile={option.avatarFile}
              avatarName={option.name}
            />
            <Typography fontSize={14}>
              {startCase(option.name)}
            </Typography>
          </div>
        </MenuItem>
      ))}
    </Select>
  );
};

export { AvatarSelect };
